import { css } from '@emotion/react'
import {
  VStack,
  Image,
  Heading,
  Center,
  Text,
  Stack,
  Grid,
  GridItem,
  Box,
  Button,
  useColorModeValue,
} from '@chakra-ui/react'
import { BsCircle } from 'react-icons/bs'
import { ImCross } from 'react-icons/im'
import type { Quiz, Stats } from 'types/generated/graphql'

const ImageWrapper = css`
  padding-top: 100%;
  position: relative;
  height: 100%;
`
const ImageBox = css`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f3b0ae;
`

type Props = {
  quiz: Quiz & { answerCandidates: string[] }
  quizNo: number
  quizzesCount: number
  quizStats?: Stats
  answer: string
  onNext: () => void
}

export const Answered: React.FC<Props> = ({
  quiz,
  quizNo,
  quizzesCount,
  quizStats,
  answer,
  onNext,
}) => {
  const buttonNextColor = useColorModeValue('buttonNextLight', 'buttonNextDark')
  const buttonColor = useColorModeValue('choiceButton', 'choiceButton')

  return (
    <VStack w="100%">
      <Heading fontSize="xl" mb="1rem">
        問題{quizNo}/{quizzesCount}
      </Heading>
      {answer === quiz.content.correct ? (
        <Heading as="h3" size="lg" mb="1rem" color="red">
          正解
        </Heading>
      ) : (
        <Heading as="h3" size="lg" mb="1rem" color="#4169e1">
          不正解
        </Heading>
      )}
      <Box w={{ base: '100%', sm: '60%', lg: '50%' }} mb="0.5rem">
        <Box css={ImageWrapper}>
          <Image
            css={ImageBox}
            src={
              quiz.content.answerImageUrl ||
              quiz.content.imageUrl ||
              '/images/quiz_bg.png'
            }
            alt="リスト画像"
          />
        </Box>
      </Box>
      <Text fontSize="lg" mb="1rem">
        Q：{quiz.content.question}
        {quizStats &&
          // statsには[正解、不正解1、不正解2…]の順番で入る
          `(正答率${quizStats.stats[0].percent})`}
      </Text>
      <Stack w="100%" mb="2rem" spacing={4} direction="row" align="center">
        <Grid
          w="100%"
          templateRows={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          {quiz.answerCandidates.map((candidate, index) => {
            return (
              <GridItem
                w="100%"
                rowSpan={{ base: 1, md: 1 }}
                colSpan={{ base: 1, md: 2 }}
                colorscheme="teal"
                key={'quizPlaylistAnswer-' + index}
              >
                <Box
                  boxShadow="base"
                  p="4"
                  rounded="md"
                  bg={buttonColor}
                  w="100%"
                >
                  {(() => {
                    // 正解
                    if (candidate === quiz.content.correct) {
                      return (
                        <Center color="red">
                          <BsCircle />：
                          <Text fontWeight="bold">{candidate}</Text>
                        </Center>
                      )
                    }

                    // 間違い
                    if (answer === candidate) {
                      return (
                        <Center color="#4169e1">
                          <ImCross />：{candidate}
                        </Center>
                      )
                    }

                    // 未選択
                    return <Center>{candidate}</Center>
                  })()}
                </Box>
              </GridItem>
            )
          })}
        </Grid>
      </Stack>

      {quiz.content.description && (
        <Center py={5}>
          <VStack>
            <Text
              fontSize={{ base: 'md', sm: 'lg' }}
              mb="0.5rem"
              fontWeight="bold"
            >
              解説
            </Text>
            <Text>{quiz.content.description}</Text>
          </VStack>
        </Center>
      )}

      <Button
        backgroundColor={buttonNextColor}
        w="200px"
        mb={3}
        onClick={onNext}
      >
        次へ
      </Button>
    </VStack>
  )
}
